import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Phone, Mail, Users, FileText, MessageSquare, 
  CheckCircle2, Pencil, Trash2, Plus, Activity, Loader2
} from "lucide-react";
import { useDealTasks } from "@/hooks/useDealTasks";
import { useDealActivities } from "@/hooks/useDealActivities";
import { 
  DealTask, DealActivity, TASK_PRIORITIES, ACTIVITY_TYPES, ACTIVITY_OUTCOMES, 
  ActivityType, TaskPriority 
} from "@/types/dealTracker";
import { format, parseISO } from "date-fns";
import AddTaskModal from "./AddTaskModal";
import LogActivityModal from "./LogActivityModal";

const ACTIVITY_ICON_MAP: Record<string, React.ElementType> = {
  phone_call: Phone,
  email: Mail,
  meeting: Users,
  note: FileText,
  text_message: MessageSquare,
};

interface FeedEntry {
  id: string;
  type: 'task' | 'activity';
  title: string;
  date: string | null;
  time: string | null;
  icon: React.ElementType;
  badgeLabel: string;
  badgeColor: string;
  isCompleted?: boolean;
  raw: DealTask | DealActivity;
}

const DealActivityFeed = ({ leadId }: { leadId: string }) => {
  const { tasks, isLoading: tasksLoading, createTask, updateTask, toggleTask, deleteTask } = useDealTasks(leadId);
  const { activities, isLoading: activitiesLoading, createActivity, updateActivity, deleteActivity } = useDealActivities(leadId);

  const [showAddTask, setShowAddTask] = useState(false);
  const [showLogActivity, setShowLogActivity] = useState(false);
  const [editingTask, setEditingTask] = useState<DealTask | null>(null);
  const [editingActivity, setEditingActivity] = useState<DealActivity | null>(null);

  const isLoading = tasksLoading || activitiesLoading;

  // Normalize and merge
  const feedEntries: FeedEntry[] = [
    ...tasks.map((t): FeedEntry => {
      const pri = TASK_PRIORITIES.find(p => p.value === t.priority);
      return {
        id: t.id,
        type: 'task',
        title: t.title,
        date: t.due_date || null,
        time: t.due_time || null,
        icon: CheckCircle2,
        badgeLabel: pri?.label || t.priority,
        badgeColor: pri?.color || 'bg-gray-500',
        isCompleted: t.is_completed,
        raw: t,
      };
    }),
    ...activities.map((a): FeedEntry => {
      const actType = ACTIVITY_TYPES.find(at => at.value === a.activity_type);
      const outcomeInfo = a.outcome ? ACTIVITY_OUTCOMES.find(o => o.value === a.outcome) : null;
      return {
        id: a.id,
        type: 'activity',
        title: a.title,
        date: a.activity_date,
        time: a.activity_time || null,
        icon: ACTIVITY_ICON_MAP[a.activity_type] || Activity,
        badgeLabel: outcomeInfo?.label || actType?.label || a.activity_type,
        badgeColor: outcomeInfo?.color || 'bg-muted',
        raw: a,
      };
    }),
  ].sort((a, b) => {
    const dateA = a.date ? new Date(a.date).getTime() : 0;
    const dateB = b.date ? new Date(b.date).getTime() : 0;
    return dateB - dateA;
  });

  const handleEditTask = (task: DealTask) => {
    setEditingTask(task);
    setShowAddTask(true);
  };

  const handleEditActivity = (activity: DealActivity) => {
    setEditingActivity(activity);
    setShowLogActivity(true);
  };

  const handleTaskSubmit = async (data: { title: string; description?: string; due_date?: string; due_time?: string; priority: TaskPriority }) => {
    if (editingTask) {
      await updateTask.mutateAsync({ id: editingTask.id, ...data });
    } else {
      await createTask.mutateAsync(data);
    }
    setEditingTask(null);
  };

  const handleActivitySubmit = async (data: { activity_type: ActivityType; title: string; activity_date: string; activity_time?: string; duration_minutes?: number; outcome?: string; notes?: string }) => {
    if (editingActivity) {
      await updateActivity.mutateAsync({ id: editingActivity.id, ...data });
    } else {
      await createActivity.mutateAsync(data);
    }
    setEditingActivity(null);
  };

  const handleDeleteEntry = (entry: FeedEntry) => {
    if (entry.type === 'task') {
      deleteTask.mutate(entry.id);
    } else {
      deleteActivity.mutate(entry.id);
    }
  };

  const handleToggleTask = (entry: FeedEntry) => {
    if (entry.type === 'task') {
      const task = entry.raw as DealTask;
      toggleTask.mutate({ id: task.id, is_completed: !task.is_completed });
    }
  };

  return (
    <>
      <Card className="md:col-span-2">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Activity Feed
            </CardTitle>
            <div className="flex gap-1.5">
              <Button 
                size="sm" 
                className="h-7 text-xs px-2 bg-[#ffd700] text-[#080c14] hover:bg-[#ffd700]/90"
                onClick={() => { setEditingActivity(null); setShowLogActivity(true); }}
              >
                <Plus className="h-3 w-3 mr-1" />
                Activity
              </Button>
              <Button 
                size="sm" 
                className="h-7 text-xs px-2 bg-[#ffd700] text-[#080c14] hover:bg-[#ffd700]/90"
                onClick={() => { setEditingTask(null); setShowAddTask(true); }}
              >
                <Plus className="h-3 w-3 mr-1" />
                Task
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : feedEntries.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No activity yet. Add a task or log an activity to get started.
            </div>
          ) : (
            <ScrollArea className="max-h-[350px] pr-2">
              <div className="relative">
                {/* Timeline vertical line */}
                <div className="absolute left-[15px] top-2 bottom-2 w-px bg-border" />

                <div className="space-y-1">
                  {feedEntries.map((entry, idx) => {
                    const IconComponent = entry.icon;
                    return (
                      <div
                        key={`${entry.type}-${entry.id}`}
                        className="group relative flex items-start gap-3 py-2.5 px-1 rounded-md hover:bg-muted/50 transition-colors"
                      >
                        {/* Icon circle */}
                        <div 
                          className={`relative z-10 flex-shrink-0 w-[30px] h-[30px] rounded-full flex items-center justify-center border-2 ${
                            entry.type === 'task' && entry.isCompleted
                              ? 'bg-green-500/20 border-green-500 text-green-500'
                              : entry.type === 'task'
                              ? 'bg-primary/10 border-primary text-primary'
                              : 'bg-muted border-border text-muted-foreground'
                          }`}
                          onClick={() => entry.type === 'task' && handleToggleTask(entry)}
                          role={entry.type === 'task' ? 'button' : undefined}
                          title={entry.type === 'task' ? 'Toggle completion' : undefined}
                          style={entry.type === 'task' ? { cursor: 'pointer' } : undefined}
                        >
                          <IconComponent className="h-3.5 w-3.5" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium truncate ${
                              entry.isCompleted ? 'line-through text-muted-foreground' : ''
                            }`}>
                              {entry.title}
                            </span>
                            <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 h-4 ${entry.badgeColor} text-white shrink-0`}>
                              {entry.badgeLabel}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-muted-foreground">
                              {entry.date ? format(parseISO(entry.date), 'MMM d, yyyy') : 'No date'}
                              {entry.time ? ` · ${entry.time}` : ''}
                            </span>
                            <span className="text-[10px] text-muted-foreground/60 uppercase">
                              {entry.type}
                            </span>
                          </div>
                        </div>

                        {/* Action buttons (visible on hover) */}
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => entry.type === 'task' 
                              ? handleEditTask(entry.raw as DealTask) 
                              : handleEditActivity(entry.raw as DealActivity)
                            }
                          >
                            <Pencil className="h-3 w-3 text-muted-foreground" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => handleDeleteEntry(entry)}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      <AddTaskModal
        open={showAddTask}
        onOpenChange={(v) => { if (!v) setEditingTask(null); setShowAddTask(v); }}
        onSubmit={handleTaskSubmit}
        isSubmitting={createTask.isPending || updateTask.isPending}
        initialData={editingTask}
      />
      <LogActivityModal
        open={showLogActivity}
        onOpenChange={(v) => { if (!v) setEditingActivity(null); setShowLogActivity(v); }}
        onSubmit={handleActivitySubmit}
        isSubmitting={createActivity.isPending || updateActivity.isPending}
        initialData={editingActivity}
      />
    </>
  );
};

export default DealActivityFeed;
